import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  ELEMENT_LAYOUT,
  MONTSERRAT_LINE_HEIGHT,
  SIMPLE_LAYOUT,
  SIMPLE_LINE_WIDTHS,
} from "./layout/constants";
import { boxPos, centerX as toLabelCenterX, textPos } from "./labelGeometry";
import { LABEL_BOX, getFrameAsset } from "./labelAssets";
import { getVisualStyle } from "./labelVisual";
import { pickLayout } from "./pickLayout";
import {
  autographLineText,
  gradeDisplayCenterX,
  gradeLabelFontSize,
  gradeLineText,
} from "./gradeHelpers";
import { softClampFieldForCard } from "./limitString";
import { measureScreenTextWidth } from "./measureScreenText";
import { generateQrDataUrl } from "./qrCode";
import { PLATE_MM, artFacePt, mmToPt } from "./layout/artGeometryPt";
import { DEFAULT_GEOMETRY } from "../../artLabels/artLabelPreviewMath";
import { DEFAULT_PLATES } from "./artPlates";
import SlabMock from "./SlabMock";
import { gradeText, subgradesLine } from "./ArtExtendedLabel";

const NO_SLOT = { x_offset: 0, y_offset: 0 };

/** Carta fora da seleção fica visível mas não editável (paridade com a tela antiga). */
const ReadOnlyContext = createContext(false);

const MONTSERRAT_BOLD = { family: '"Montserrat-Bold"', weight: "normal" };
const MONTSERRAT_LIGHT = { family: '"Montserrat-Light"', weight: "normal" };
const MONTSERRAT_REGULAR = { family: '"Montserrat-Regular"', weight: "normal" };
// Fonte CSS da família declarada no layout (constants.js): a tela desenha com a
// mesma face que o PDF registra e que o fontkit mede.
const fontForFamily = (family) =>
  /Light/i.test(family) ? MONTSERRAT_LIGHT : MONTSERRAT_BOLD;

const baseTextStyle = (font, fontSize, color) => ({
  position: "absolute",
  fontFamily: font.family,
  fontWeight: font.weight,
  fontSize,
  // ascent+descent: zera o half-leading, então a baseline cai no mesmo ponto
  // que no PDF (topo da caixa + ascender).
  lineHeight: MONTSERRAT_LINE_HEIGHT,
  color,
  whiteSpace: "pre",
  // Inputs herdam Open Sans do body se a face custom não estiver pronta;
  // forçar evita flash da fonte errada.
  WebkitFontSmoothing: "antialiased",
});

/** Input transparente ocupando exatamente o espaço do texto no PDF. */
const FieldInput = ({
  value,
  onChange,
  font,
  fontSize,
  color,
  left,
  top,
  width,
  align = "left",
  overflow,
  ariaLabel,
  dataField,
}) => {
  const readOnly = useContext(ReadOnlyContext);
  const measured = measureScreenTextWidth(
    value || "",
    font.family.replace(/"/g, ""),
    fontSize,
    font.weight
  );
  // Direita: largura colada no texto, âncora na borda direita (senão o teto da
  // área útil — ex. numeração ~37 pt — esconde o valor enquanto edita).
  const contentWidth = Math.max(measured + fontSize * 0.6, fontSize * 2);
  const inputWidth = align === "right" ? contentWidth : width ?? contentWidth;
  const boxLeft = align === "right" ? left - inputWidth : left;

  return (
    <input
      aria-label={ariaLabel}
      data-tag-error-field={dataField}
      value={value ?? ""}
      readOnly={readOnly}
      onChange={(event) => onChange(event.target.value)}
      spellCheck={false}
      style={{
        ...baseTextStyle(font, fontSize, color),
        left: boxLeft,
        top,
        width: inputWidth,
        textAlign: align,
        padding: 0,
        margin: 0,
        border: "none",
        outline: overflow ? "1px solid #dc2626" : "none",
        background: overflow ? "rgba(220,38,38,0.15)" : "transparent",
        borderRadius: 1,
        // Alguns browsers herdam cor do tema no input; força o fill.
        WebkitTextFillColor: color,
        caretColor: color,
      }}
    />
  );
};

const StaticText = ({ children, font, fontSize, color, left, top, width, align = "left" }) => {
  if (children === null || children === undefined || String(children).trim() === "") return null;
  // Centro: ancora no próprio eixo e traduz −50% da largura do texto (mais
  // estável que textAlign numa caixa larga, que em alguns browsers falha).
  const centered = align === "center";
  const rightAligned = align === "right" && width;
  return (
    <span
      style={{
        ...baseTextStyle(font, fontSize, color),
        left: rightAligned ? left - width : left,
        top,
        // A caixa do texto à direita cobre a linha inteira (paridade com o
        // PDF) e é pintada depois dos inputs: sem isto ela vira um escudo que
        // engole o clique do campo editável da mesma linha.
        pointerEvents: "none",
        ...(centered
          ? { transform: "translateX(-50%)", textAlign: "center", whiteSpace: "nowrap" }
          : { width, textAlign: align }),
      }}
    >
      {String(children)}
    </span>
  );
};

/** Parte editável de um composto de texto corrido, largura colada no texto. */
const InlineInput = ({ value, onChange, color, ariaLabel, font = MONTSERRAT_LIGHT, fontSize = 4.5 }) => {
  const readOnly = useContext(ReadOnlyContext);
  const width = Math.max(
    measureScreenTextWidth(value || "", font.family.replace(/"/g, ""), fontSize, font.weight) + 1,
    4
  );
  return (
    <input
      aria-label={ariaLabel}
      value={value ?? ""}
      readOnly={readOnly}
      onChange={(event) => onChange(event.target.value)}
      spellCheck={false}
      style={{
        fontFamily: font.family,
        fontSize,
        lineHeight: 1,
        color,
        width,
        padding: 0,
        margin: 0,
        border: "none",
        outline: "none",
        background: "transparent",
      }}
    />
  );
};

const WithSubgradesFields = ({ card, textColor, overflowKeys, onField, qrDataUrl }) => {
  const gradeCenter = toLabelCenterX(gradeDisplayCenterX(NO_SLOT));
  const nomeNotaSize = gradeLabelFontSize(card.notaGeral);

  const at = (key, fontSize) => textPos(ELEMENT_LAYOUT[key], fontSize);
  const nomePos = at("nome", 8.5);
  const editionPos = at("edition", 4.5);
  const raridadePos = at("raridade", 4.5);
  const infosPos = at("infos", 4.5);
  const notaGeralPos = at("nota_geral", 27);
  const nomeNotaPos = at("nome_nota", nomeNotaSize);
  const serialPos = at("serial", 5.5);

  return (
    <>
      <FieldInput
        value={card.nome}
        onChange={(next) =>
          onField("nome", softClampFieldForCard("nome", next, card, card.nome))
        }
        font={MONTSERRAT_BOLD}
        fontSize={8.5}
        color={textColor}
        left={nomePos.left}
        top={nomePos.top}
        overflow={overflowKeys?.has?.("nome")}
        ariaLabel="Nome"
        dataField={`${card.serial}::nome`}
      />
      <FieldInput
        value={card.edition}
        onChange={(next) =>
          onField("edition", softClampFieldForCard("edicao", next, card, card.edition))
        }
        font={MONTSERRAT_LIGHT}
        fontSize={4.5}
        color={textColor}
        left={editionPos.left}
        top={editionPos.top}
        overflow={overflowKeys?.has?.("edicao")}
        ariaLabel="Edição"
        dataField={`${card.serial}::edicao`}
      />
      <FieldInput
        value={card.raridade}
        onChange={(next) =>
          onField("raridade", softClampFieldForCard("raridade", next, card, card.raridade))
        }
        font={MONTSERRAT_LIGHT}
        fontSize={4.5}
        color={textColor}
        left={raridadePos.left}
        top={raridadePos.top}
        overflow={overflowKeys?.has?.("raridade")}
        ariaLabel="Raridade"
        dataField={`${card.serial}::raridade`}
      />

      <span
        data-tag-error-field={`${card.serial}::infos`}
        style={{
          ...baseTextStyle(MONTSERRAT_LIGHT, 4.5, textColor),
          left: infosPos.left,
          top: infosPos.top,
          display: "flex",
          alignItems: "flex-start",
          outline: overflowKeys?.has?.("infos") ? "1px solid #dc2626" : "none",
          background: overflowKeys?.has?.("infos") ? "rgba(220,38,38,0.15)" : "transparent",
        }}
      >
        <span>| </span>
        <InlineInput
          value={card.ano}
          onChange={(next) =>
            onField("ano", softClampFieldForCard("ano", next, card, card.ano))
          }
          color={textColor}
          ariaLabel="Ano"
        />
        <span> | </span>
        <InlineInput
          value={card.idioma}
          onChange={(next) =>
            onField("idioma", softClampFieldForCard("idioma", next, card, card.idioma))
          }
          color={textColor}
          ariaLabel="Idioma"
        />
        <span> | </span>
        <InlineInput
          value={card.num}
          onChange={(next) =>
            onField("num", softClampFieldForCard("num", next, card, card.num))
          }
          color={textColor}
          ariaLabel="Numeração"
        />
      </span>

      <StaticText
        font={MONTSERRAT_LIGHT}
        fontSize={6.5}
        color={textColor}
        left={at("surface", 6.5).left}
        top={at("surface", 6.5).top}
      >
        {`Surface - ${card.notaSurface}`}
      </StaticText>
      <StaticText
        font={MONTSERRAT_LIGHT}
        fontSize={6.5}
        color={textColor}
        left={at("centering", 6.5).left}
        top={at("centering", 6.5).top}
      >
        {`Centering - ${card.notaCenter}`}
      </StaticText>
      <StaticText
        font={MONTSERRAT_LIGHT}
        fontSize={6.5}
        color={textColor}
        left={at("edges", 6.5).left}
        top={at("edges", 6.5).top}
      >
        {`Edges - ${card.notaEdge}`}
      </StaticText>
      <StaticText
        font={MONTSERRAT_LIGHT}
        fontSize={6.5}
        color={textColor}
        left={at("corners", 6.5).left}
        top={at("corners", 6.5).top}
      >
        {`Corners - ${card.notaCorner}`}
      </StaticText>

      <StaticText
        font={MONTSERRAT_BOLD}
        fontSize={27}
        color={textColor}
        left={gradeCenter}
        top={notaGeralPos.top}
        align="center"
      >
        {card.notaGeral}
      </StaticText>
      <StaticText
        font={MONTSERRAT_LIGHT}
        fontSize={nomeNotaSize}
        color={textColor}
        left={gradeCenter}
        top={nomeNotaPos.top}
        align="center"
      >
        {card.nome_nota}
      </StaticText>
      <StaticText
        font={MONTSERRAT_LIGHT}
        fontSize={5.5}
        color={textColor}
        left={gradeCenter}
        top={serialPos.top}
        align="center"
      >
        {card.serial}
      </StaticText>
      <QrPreview layout={ELEMENT_LAYOUT.qr} dataUrl={qrDataUrl} />
    </>
  );
};

const SimpleFields = ({ card, textColor, overflowKeys, onField, scenario, qrDataUrl }) => {
  const showAuthentic = scenario === "authentic" || scenario === "authentic_auto";
  const showGrade = scenario === "no_subgrades" || scenario === "no_subgrades_auto";
  const showAuto = scenario === "authentic_auto" || scenario === "no_subgrades_auto";

  const at = (key) => {
    const layout = SIMPLE_LAYOUT[key];
    const [fontFamily, fontSize] = layout.font;
    return {
      ...textPos(layout, fontSize),
      fontSize,
      font: fontForFamily(fontFamily),
      // Direita: caixa = linha inteira, igual ao PDF (nunca quebra nem vaza).
      width: SIMPLE_LINE_WIDTHS[key],
    };
  };

  const editable = (key, value, field, clampKey, overflowKey) => {
    const pos = at(key);
    return (
      <FieldInput
        value={value}
        onChange={(next) =>
          onField(field, softClampFieldForCard(clampKey, next, card, value))
        }
        font={pos.font}
        fontSize={pos.fontSize}
        color={textColor}
        left={pos.left}
        top={pos.top}
        overflow={overflowKeys?.has?.(overflowKey)}
        ariaLabel={field}
        dataField={`${card.serial}::${overflowKey}`}
      />
    );
  };

  const readonly = (key, value, align = "left") => {
    const pos = at(key);
    return (
      <StaticText
        font={pos.font}
        fontSize={pos.fontSize}
        color={textColor}
        left={pos.left}
        top={pos.top}
        width={align === "right" ? pos.width : undefined}
        align={align}
      >
        {value}
      </StaticText>
    );
  };

  // Ano / idioma / numeração: área útil individual (não o composto infos).
  const simpleInput = (key, field, value, overflowKey, align = "left") => {
    const pos = at(key);
    return (
      <FieldInput
        value={value}
        onChange={(next) =>
          onField(field, softClampFieldForCard(overflowKey, next, card, value))
        }
        font={pos.font}
        fontSize={pos.fontSize}
        color={textColor}
        left={pos.left}
        top={pos.top}
        width={align === "right" ? pos.width : undefined}
        align={align}
        overflow={overflowKeys?.has?.(overflowKey)}
        ariaLabel={field}
        dataField={`${card.serial}::${overflowKey}`}
      />
    );
  };

  return (
    <>
      {editable("name", card.nome, "nome", "nome", "nome")}
      {editable("edition", card.edition, "edition", "edicao", "edicao")}
      {showAuthentic ? readonly("right_top", "AUTHENTIC", "right") : null}
      {simpleInput("number", "num", card.num, "numeracao", "right")}
      {editable("raridade", card.raridade, "raridade", "raridade", "raridade")}
      {showGrade ? readonly("right_middle", gradeLineText(card), "right") : null}
      {simpleInput("ano", "ano", card.ano, "ano")}
      {showAuto ? readonly("right_auto", autographLineText(card.notaAutografo), "right") : null}
      {simpleInput("idioma", "idioma", card.idioma, "idioma")}
      {readonly("serial", card.serial, "right")}
      <QrPreview layout={SIMPLE_LAYOUT.qr} dataUrl={qrDataUrl} />
    </>
  );
};

/** QR na mesma posição/rotação do PDF (verso da dobra). */
const QrPreview = ({ layout, dataUrl }) => {
  if (!dataUrl) return null;
  const pos = boxPos(layout);
  return (
    <img
      alt=""
      src={dataUrl}
      draggable={false}
      style={{
        position: "absolute",
        left: pos.left,
        top: pos.top,
        width: pos.width,
        height: pos.height,
        transform: "rotate(180deg)",
        pointerEvents: "none",
        userSelect: "none",
      }}
    />
  );
};

/** Metade da etiqueta: dobra no meio (frente = topo, verso = base). */
const LABEL_FACE_HEIGHT = LABEL_BOX.height / 2;

/** Cor do texto sobre cada placa: branco no escuro, preto no claro (paridade com o PDF). */
const plateTextColor = (tone) => (tone === "light" ? "#000000" : "#FFFFFF");

/**
 * Caixas das 4 placas do papel "art" em pt, relativas ao CORTE da frente
 * (labelWidth × frontHeight, sem sangria): PLATE_MM convertido com uma única escala
 * (mm → pt), a mesma de platesPt no PDF — o editor mostra só o que vai impresso.
 */
const artPlateBoxes = () => {
  const out = {};
  for (const [key, p] of Object.entries(PLATE_MM)) {
    out[key] = { left: mmToPt(p.x), top: mmToPt(p.y), width: mmToPt(p.w), height: mmToPt(p.h) };
  }
  return out;
};

/**
 * Posiciona o PNG aprovado (canvas com sangria, labelWidth + 2·bleed mm de largura) em pt
 * reais: a sangria sai igual pelos quatro lados e o corte ocupa exatamente a frente.
 * Altura `auto` preserva a proporção do canvas (como o drawImage do PDF). `maxWidth: none`
 * anula o preflight do Tailwind (`img { max-width: 100% }`), que encolhia o canvas para a
 * largura da frente e deixava a sangria da direita em branco.
 */
const artFrontImageStyle = (geometry) => {
  const g = { ...DEFAULT_GEOMETRY, ...(geometry || {}) };
  const bleed = mmToPt(g.bleed);
  return {
    position: "absolute",
    left: -bleed,
    top: -bleed,
    width: mmToPt(g.labelWidth + 2 * g.bleed),
    height: "auto",
    maxWidth: "none",
    maxHeight: "none",
    pointerEvents: "none",
    userSelect: "none",
  };
};

/** Placa semitransparente (preto 0,55 / branco 0,70), cantos arredondados — paridade com o PDF. */
const PlateBackground = ({ box, tone, name }) => (
  <div
    data-testid={name ? `art-plate-${name}` : undefined}
    style={{
      position: "absolute",
      left: box.left,
      top: box.top,
      width: box.width,
      height: box.height,
      backgroundColor: tone === "light" ? "#FFFFFF" : "#000000",
      opacity: tone === "light" ? 0.7 : 0.55,
      borderRadius: 3,
    }}
  />
);

/**
 * Campos do papel "art": o fundo (PNG aprovado) é desenhado fora deste
 * componente; aqui ficam as 4 placas semitransparentes com os textos por
 * cima — nome editável, nota/subgrades/meta com o mesmo texto do PDF
 * (subgradesLine/metaLine de ArtExtendedLabel), edição e idioma editáveis
 * dentro da meta (mesmo padrão do composto `| ano | idioma | num` legado).
 */
const ArtExtendedFields = ({ card, geometry, overflowKeys, onField, qrDataUrl }) => {
  const plates = { ...DEFAULT_PLATES, ...(card.art?.plates || {}) };
  const boxes = artPlateBoxes();
  const face = artFacePt(geometry);
  const nameColor = plateTextColor(plates.name);
  const gradeColor = plateTextColor(plates.grade);
  const subgradesColor = plateTextColor(plates.subgrades);
  const metaColor = plateTextColor(plates.meta);

  const vCenter = (box, fontSize) =>
    box.top + Math.max((box.height - fontSize * MONTSERRAT_LINE_HEIGHT) / 2, 0);

  const nameFontSize = 10;
  const gradeFontSize = 24;
  const subgradesFontSize = 7;
  const metaFontSize = 6.5;

  return (
    <>
      <PlateBackground box={boxes.name} tone={plates.name} name="name" />
      <PlateBackground box={boxes.grade} tone={plates.grade} name="grade" />
      <PlateBackground box={boxes.subgrades} tone={plates.subgrades} name="subgrades" />
      <PlateBackground box={boxes.meta} tone={plates.meta} name="meta" />

      <FieldInput
        value={card.nome}
        onChange={(next) => onField("nome", softClampFieldForCard("nome", next, card, card.nome))}
        font={MONTSERRAT_BOLD}
        fontSize={nameFontSize}
        color={nameColor}
        left={boxes.name.left + 2}
        top={vCenter(boxes.name, nameFontSize)}
        overflow={overflowKeys?.has?.("nome")}
        ariaLabel="Nome"
        dataField={`${card.serial}::nome`}
      />

      <StaticText
        font={MONTSERRAT_BOLD}
        fontSize={gradeFontSize}
        color={gradeColor}
        left={boxes.grade.left + boxes.grade.width / 2}
        top={vCenter(boxes.grade, gradeFontSize)}
        align="center"
      >
        {gradeText(card)}
      </StaticText>

      <StaticText
        font={MONTSERRAT_REGULAR}
        fontSize={subgradesFontSize}
        color={subgradesColor}
        left={boxes.subgrades.left + 2}
        top={vCenter(boxes.subgrades, subgradesFontSize)}
      >
        {subgradesLine(card)}
      </StaticText>

      <span
        data-tag-error-field={`${card.serial}::meta`}
        style={{
          ...baseTextStyle(MONTSERRAT_REGULAR, metaFontSize, metaColor),
          left: boxes.meta.left + 2,
          top: vCenter(boxes.meta, metaFontSize),
          display: "flex",
          alignItems: "flex-start",
        }}
      >
        <InlineInput
          value={card.edition}
          onChange={(next) =>
            onField("edition", softClampFieldForCard("edicao", next, card, card.edition))
          }
          color={metaColor}
          ariaLabel="Edição"
          font={MONTSERRAT_REGULAR}
          fontSize={metaFontSize}
        />
        <span> · </span>
        <InlineInput
          value={card.idioma}
          onChange={(next) =>
            onField("idioma", softClampFieldForCard("idioma", next, card, card.idioma))
          }
          color={metaColor}
          ariaLabel="Idioma"
          font={MONTSERRAT_REGULAR}
          fontSize={metaFontSize}
        />
        <span>{` · ${card.serial}`}</span>
      </span>

      {qrDataUrl ? (
        <img
          alt=""
          src={qrDataUrl}
          draggable={false}
          style={{
            position: "absolute",
            left: (face.width - 40) / 2,
            top: face.height + (face.backHeight - 40) / 2,
            width: 40,
            height: 40,
            transform: "rotate(180deg)",
            pointerEvents: "none",
            userSelect: "none",
          }}
        />
      ) : null}
    </>
  );
};

/** Frente do papel "art" em pt do corte real (artFacePt): PNG (sangria fora), verso branco e placas. */
const ArtFace = ({ card, geometry, overflowKeys, onField, qrDataUrl }) => {
  const face = artFacePt(geometry);
  return (
  <>
    {/* Frente: só o corte impresso do PNG aprovado (sangria fora, área da carta coberta pelo verso). */}
    <img
      alt=""
      data-testid="art-background"
      src={card.art?.url}
      draggable={false}
      style={artFrontImageStyle(geometry)}
    />
    {/* Verso: fundo branco (paridade com composeSheetWithUnits, que pinta o verso de branco). */}
    <div
      style={{
        position: "absolute",
        left: 0,
        top: face.height,
        width: face.width,
        height: face.backHeight,
        backgroundColor: "#FFFFFF",
      }}
    />
    <ArtExtendedFields
      card={card}
      geometry={geometry}
      overflowKeys={overflowKeys}
      onField={onField}
      qrDataUrl={qrDataUrl}
    />
  </>
  );
};

const noop = () => {};

/**
 * Frente só leitura para quem simula a etiqueta fora do gerador (revisão da arte, spec
 * 2026-10-01 R3): mesmas placas e textos do editor, sem QR (o verso não aparece no slab).
 */
const ArtFacePreview = ({ card, geometry }) => {
  const g = useMemo(() => ({ ...DEFAULT_GEOMETRY, ...(geometry || {}) }), [geometry]);
  return (
    <ReadOnlyContext.Provider value>
      <ArtFace card={card} geometry={g} overflowKeys={null} onField={noop} qrDataUrl={null} />
    </ReadOnlyContext.Provider>
  );
};

/**
 * Etiqueta editável em tamanho real (pt) escalada por `scale`.
 * Por padrão mostra só a frente (metade de cima, editável). O verso (QR /
 * branding) aparece ao girar — conteúdo da base já impresso a 180°, então
 * rotacionamos o viewport para ler como na peça dobrada.
 */
const LabelCardEditor = ({ card, scale = 3, overflowKeys = null, onField, readOnly = false, artGeometry = null }) => {
  const visual = card?.visual || "majorca_legado";
  const isArtExtended = visual === "art_extended";
  // geometria da etiqueta art: do gerador (backend) > da carta (PDF) > default local
  const geometry = useMemo(
    () => ({ ...DEFAULT_GEOMETRY, ...(artGeometry || card?.artGeometry || {}) }),
    [artGeometry, card?.artGeometry]
  );
  const [view, setView] = useState("label");
  const showingSlab = isArtExtended && view === "slab";
  const layout = card?.scenario ? pickLayout(card.scenario) : null;
  const { textColor, fillColor } = getVisualStyle(visual);
  const frameSrc = useMemo(() => getFrameAsset(visual), [visual]);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [face, setFace] = useState("front");
  const showingBack = face === "back";

  useEffect(() => {
    let cancelled = false;
    if (!card?.serial) {
      setQrDataUrl(null);
      return undefined;
    }
    generateQrDataUrl(card.serial)
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setQrDataUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [card?.serial]);

  // Art: frente no corte real (pt) com a mesma largura de tela do LABEL_BOX legado;
  // demais visuais seguem no LABEL_BOX (frente = metade de cima).
  const artFace = isArtExtended ? artFacePt(geometry) : null;
  const contentW = artFace ? artFace.width : LABEL_BOX.width;
  const contentH = artFace ? artFace.height + artFace.backHeight : LABEL_BOX.height;
  const frontH = artFace ? artFace.height : LABEL_FACE_HEIGHT;
  const backH = artFace ? artFace.backHeight : LABEL_FACE_HEIGHT;
  const viewScale = artFace ? (scale * LABEL_BOX.width) / artFace.width : scale;
  const viewportW = contentW * viewScale;
  const viewportH = (showingBack ? backH : frontH) * viewScale;

  // Conteúdo da etiqueta em tamanho real (pt), sem escala: reutilizado pela vista
  // normal (frente/verso) e pela simulação no slab.
  const faceContent = isArtExtended ? (
    <ArtFace card={card} geometry={geometry} overflowKeys={overflowKeys} onField={onField} qrDataUrl={qrDataUrl} />
  ) : (
    <>
      <img
        alt=""
        src={frameSrc}
        draggable={false}
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          width: LABEL_BOX.width,
          height: LABEL_BOX.height,
          pointerEvents: "none",
          userSelect: "none",
        }}
      />
      {layout === "with_subgrades" ? (
        <WithSubgradesFields
          card={card}
          textColor={textColor}
          overflowKeys={overflowKeys}
          onField={onField}
          qrDataUrl={qrDataUrl}
        />
      ) : layout ? (
        <SimpleFields
          card={card}
          textColor={textColor}
          overflowKeys={overflowKeys}
          onField={onField}
          scenario={card.scenario}
          qrDataUrl={qrDataUrl}
        />
      ) : null}
    </>
  );

  return (
    <ReadOnlyContext.Provider value={readOnly || showingBack || showingSlab}>
      <div className="flex flex-col gap-1.5 items-start">
        <div className="flex items-center gap-2">
          {!showingSlab ? (
            <button
              type="button"
              className="text-xs px-2 py-1 rounded border border-[#d59106] bg-[#111] text-[#ffedd5] hover:bg-[#2a2a2a]"
              onClick={() => setFace((f) => (f === "front" ? "back" : "front"))}
              aria-pressed={showingBack}
              aria-label={showingBack ? "Ver frente da etiqueta" : "Ver verso da etiqueta"}
            >
              {showingBack ? "Ver frente" : "Ver verso"}
            </button>
          ) : null}
          {isArtExtended ? (
            <button
              type="button"
              className="text-xs px-2 py-1 rounded border border-[#d59106] bg-[#111] text-[#ffedd5] hover:bg-[#2a2a2a]"
              onClick={() => setView((v) => (v === "slab" ? "label" : "slab"))}
              aria-pressed={showingSlab}
              aria-label={showingSlab ? "Ver só a etiqueta" : "Ver como fica no slab"}
            >
              {showingSlab ? "Ver etiqueta" : "Ver no slab"}
            </button>
          ) : null}
          <span className="text-[11px] text-[#d59106]/80">
            {showingSlab
              ? "Simulação no slab (proporção CGC, escala real entre etiqueta e carta)"
              : showingBack
                ? "Verso (após a dobra)"
                : "Frente (editável)"}
          </span>
        </div>

        {showingSlab ? (
          <SlabMock
            geometry={geometry}
            pxPerMm={(LABEL_BOX.width * scale) / geometry.labelWidth}
            faceSize={artFace}
            cardImageUrl={card.art?.sourceUrl || null}
          >
            {faceContent}
          </SlabMock>
        ) : (
          <div
            data-testid="label-viewport"
            style={{
              width: viewportW,
              height: viewportH,
              overflow: "hidden",
              position: "relative",
              flexShrink: 0,
              // Base impressa de cabeça para baixo; girar o recorte deixa o verso legível.
              transform: showingBack ? "rotate(180deg)" : undefined,
              backgroundColor: fillColor || "#FFFFFF",
            }}
          >
            <div
              style={{
                width: contentW,
                height: contentH,
                position: "absolute",
                left: 0,
                top: 0,
                transform: showingBack
                  ? `scale(${viewScale}) translateY(-${frontH}px)`
                  : `scale(${viewScale})`,
                transformOrigin: "top left",
                backgroundColor: fillColor || "#FFFFFF",
              }}
            >
              {faceContent}
            </div>
          </div>
        )}
      </div>
    </ReadOnlyContext.Provider>
  );
};

export default LabelCardEditor;
export { LABEL_BOX, LABEL_FACE_HEIGHT, ArtFacePreview };
